import { FunctionalComponent, h } from 'preact';
import { ChangeEvent } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import { cloneDeep } from 'lodash';
import { isValidEmail } from '@uw/utils';
import { headerStore } from 'global/header.store';
import { useTranslation } from 'hooks';
import { Button } from 'components/button';
import {
  EMPTY_EMAIL_WARNING,
  EMPTY_PASSWORD_WARNING,
  INCORRECT_EMAIL_WARNING,
  initialFormState,
} from './constants';
import { LoginForm } from './types';
import './login.scss';

export const Login: FunctionalComponent = () => {
  const { translate } = useTranslation();
  const [form, setForm] = useState<LoginForm>(initialFormState);
  const isEmailInvalid = form.email.error;
  const emailErrorClassName = isEmailInvalid ? 'uw-login__form-group_error' : '';
  const filledEmailClassName = form.email.value ? 'uw-login__form-group_filled' : '';
  const isPasswordInvalid = form.password.error;
  const passwordErrorClassName = isPasswordInvalid ? 'uw-login__form-group_error' : '';
  const filledPasswordClassName = form.password.value ? 'uw-login__form-group_filled' : '';

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    setForm((prevState) => {
      const nextState = cloneDeep(prevState);
      nextState[name].value = value;
      return nextState;
    });
  };

  const validateEmail = () => {
    let errorWarning = '';

    if (!form.email.value) {
      errorWarning = EMPTY_EMAIL_WARNING;
    } else if (!isValidEmail(form.email.value)) {
      errorWarning = INCORRECT_EMAIL_WARNING;
    }
    if (errorWarning) {
      setForm((prevState) => {
        const nextState = cloneDeep(prevState);
        nextState.email.error = errorWarning;
        return nextState;
      });
    }
  };

  const validatePassword = () => {
    if (!form.password.value) {
      setForm((prevState) => {
        const nextState = cloneDeep(prevState);
        nextState.password.error = EMPTY_PASSWORD_WARNING;
        return nextState;
      });
    }
  };

  const resetFieldError = (e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.currentTarget;
    setForm((prevState) => {
      const nextState = cloneDeep(prevState);
      if (name in nextState) {
        nextState[name].error = null;
      }
      return nextState;
    });
  };

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    if (form.email.value && form.password.value && !isEmailInvalid && !isPasswordInvalid) {
      // send form data
    } else {
      validateEmail();
      validatePassword();
    }
  };

  useEffect(() => {
    headerStore.setBackStateToHomePage();
    headerStore.title.next('widget.login.title');
  }, []);

  return (
    <div className="uw-login">
      <div className="uw-login__logo">
        <img src="./frontend/images/login-uw-logo.svg" alt="UserWay logo" />
      </div>

      <form className="uw-login__form" name="form" novalidate onSubmit={handleSubmit}>
        <fieldset>
          <div className={`uw-login__form-group ${emailErrorClassName} ${filledEmailClassName}`}>
            <label for="email-field" id="login-email-label" className="uw-login__label">
              {translate('widget.login.labels.email')}
            </label>
            <input
              value={form.email.value}
              onChange={handleFieldChange}
              onBlur={validateEmail}
              onFocus={resetFieldError}
              id="email-field"
              name="email"
              className="uw-login__input-field"
              title={translate('widget.login.titles.enter_email')}
              autocomplete="email"
              type="email"
              aria-required="true"
              aria-describedby={isEmailInvalid ? 'login-email-err' : undefined}
              aria-invalid={!!isEmailInvalid}
            />
            {isEmailInvalid && (
              <p id="login-email-err" role="alert" className="uw-login__input-field-error">
                {form.email.error}
              </p>
            )}
          </div>
          <div
            className={`uw-login__form-group ${passwordErrorClassName} ${filledPasswordClassName}`}
          >
            <label for="password-field" id="login-pass-label" className="uw-login__label">
              {translate('widget.login.labels.password')}
            </label>
            <input
              value={form.password.value}
              onChange={handleFieldChange}
              onFocus={resetFieldError}
              onBlur={validatePassword}
              id="password-field"
              name="password"
              className="uw-login__input-field"
              aria-required="true"
              aria-describedby={isPasswordInvalid ? 'login-pass-err' : undefined}
              autocomplete="current-password"
              type="password"
              title={translate('widget.login.titles.enter_password')}
            />
            {isPasswordInvalid && (
              <p id="login-pass-err" role="alert" className="uw-login__input-field-error">
                Please enter your password
              </p>
            )}
          </div>

          <div class="uw-login__form-group uw-login__form-group_error">
            <label for="verificationCode" className="uw-login__label">
              {translate('widget.login.labels.verificationCode')}
            </label>
            <input
              id="verificationCode"
              name="verificationCode"
              className="uw-login__input-field"
              type="text"
              aria-required="true"
              aria-describedby="login-verificationCode-err"
            />
            <p id="login-verificationCode-err" role="alert" className="uw-login__input-field-error">
              Verification error
            </p>
          </div>
        </fieldset>

        <div className="uw-login__missing-account-id-alert">
          {translate('users.missing_account')}
        </div>

        <div className="uw-login__btn-row">
          <Button color="white" style={{ width: '48%' }}>
            Sign In With SSO
          </Button>
          <Button
            id="login-button-el"
            type="submit"
            aria-label={translate('widget.login.arias.login')}
            style={{ width: '48%' }}
          >
            {translate('widget.login.buttons.login')}
          </Button>
        </div>
      </form>

      <div className="uw-login__sso-container">
        <div class="uw-login__btn-row uw-login__btn-row_center">
          <Button color="white" style={{ width: '212px' }}>
            Sign In With SSO
          </Button>
        </div>
      </div>

      <p className="uw-login__forgot">
        <a href="#" class="uw-login__link">
          {translate('widget.login.buttons.forgot')}
        </a>
      </p>

      <p className="uw-login__info">
        You can also sign into the desktop dashboard at
        <span className="uw-login__info__link-wrap">
          <a href="https://manage.userway.org" class="uw-login__link" target="_blank">
            manage.userway.org
          </a>
        </span>
      </p>
    </div>
  );
};
